// src/utils/exportJson.js
//
// "Export" handler for the AppHeaderBar download button. Produces a
// full backup of the portfolio: every location plus its embedded
// children (leases, allowances, commentary, etc.) as returned by
// getLocation(id).
//
// The server assembles and sensitivity-gates the complete tree. Keeping
// assembly off the client prevents a browser from collecting financial data
// through a sequence of individually safe-looking API calls.
//   - The wrapper object includes `exportedAt` so backups can be
//     ordered/audited later without parsing the filename.

import { NetworkError, errorFromResponse } from "../data/errors.js";

/**
 * Build the export payload.
 *
 * @returns {Promise<{ exportedAt: string, locations: object[] }>}
 */
export async function buildExportPayload() {
  let response;
  try {
    response = await fetch("/api/locations/export", {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    });
  } catch (err) {
    throw new NetworkError("Failed to reach the portfolio export endpoint", err);
  }

  if (!response.ok) throw await errorFromResponse(response);
  try {
    return await response.json();
  } catch (err) {
    throw new NetworkError("Malformed JSON from the portfolio export endpoint", err);
  }
}

/**
 * Trigger a browser download of the export payload as a JSON file.
 * Filename: `ripple-locations-YYYY-MM-DDTHH-MM-SS.json`.
 *
 * Pulled out as a separate function so unit tests can call
 * buildExportPayload() without invoking jsdom-unfriendly DOM APIs.
 */
export async function downloadLocationsJson() {
  const payload = await buildExportPayload();
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const stamp = payload.exportedAt.replace(/[:.]/g, "-").replace(/T/, "T");
  const a = document.createElement("a");
  a.href = url;
  a.download = `ripple-locations-${stamp}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return payload;
}
